import { useMemo } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { resolveAvatarColor } from "@/lib/avatarColor";
import { initials, formatDuration, formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { TeamRow } from "@/api/analytics";

interface TeamPerformanceProps {
  rows?: TeamRow[];
  loading?: boolean;
  /** When set, clicking a row notifies the page to filter by this
   *  member; the row that matches the active filter renders with a
   *  subtle indigo background. */
  selectedMemberId?: string | null;
  onSelectMember?: (memberId: string | null) => void;
}

type Numeric = "companies" | "contacted" | "replied" | "won" | "calls" | "call_seconds" | "tasks_done" | "conversion_rate";

/** Headers + the field they read. Order is the table column order. */
const COLUMNS: Array<{
  key: Numeric;
  label: string;
  /** Higher is always better → emerald the per-column max. */
  bigger_is_better?: boolean;
  /** Format strategy. */
  format: "count" | "duration" | "percentage";
}> = [
  { key: "companies", label: "Companies", bigger_is_better: true, format: "count" },
  { key: "contacted", label: "Contacted", bigger_is_better: true, format: "count" },
  { key: "replied", label: "Replied", bigger_is_better: true, format: "count" },
  { key: "won", label: "Won", bigger_is_better: true, format: "count" },
  { key: "calls", label: "Calls", bigger_is_better: true, format: "count" },
  { key: "call_seconds", label: "Call time", bigger_is_better: true, format: "duration" },
  { key: "tasks_done", label: "Tasks done", bigger_is_better: true, format: "count" },
  { key: "conversion_rate", label: "Conv. rate", bigger_is_better: true, format: "percentage" },
];

export function TeamPerformance({
  rows,
  loading,
  selectedMemberId,
  onSelectMember,
}: TeamPerformanceProps) {
  // Defaults rows once at the top — the rest of the component
  // operates on `safeRows`, which is guaranteed to be an array even
  // if the page hands us undefined (partial backend response).
  const safeRows = rows ?? [];
  // Per-column max so we can highlight the leader.
  const maxByColumn = useMemo(() => {
    const out = new Map<Numeric, number>();
    for (const c of COLUMNS) {
      const max = safeRows.reduce(
        (acc, r) => Math.max(acc, (r[c.key] as number) ?? 0),
        0,
      );
      out.set(c.key, max);
    }
    return out;
  }, [safeRows]);

  // Conversion rate gets a small horizontal bar — capped at the max
  // observed value so the leader's bar fills 100% of the cell.
  const maxConv = maxByColumn.get("conversion_rate") ?? 0;

  if (loading) {
    return <div className="h-[200px] animate-pulse rounded-plane bg-slate-100" />;
  }
  if (safeRows.length === 0) {
    return (
      <p className="text-sm italic text-slate-400">
        No team activity in this period.
      </p>
    );
  }
  return (
    <div className="overflow-hidden rounded-plane bg-white shadow-plane">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="sticky left-0 bg-slate-50 px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500">
                Member
              </th>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className="px-3 py-2.5 text-right text-[10px] font-medium uppercase tracking-wider text-slate-500"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-900/[0.06]">
            {safeRows.map((row, rowIdx) => {
              const isSelected = selectedMemberId === row.member_id;
              const interactive = !!onSelectMember;
              const avatar = resolveAvatarColor(
                row.avatar_color,
                row.member_id,
              );
              return (
                <tr
                  // member_id might be missing for legacy rows; fall
                  // back to the row index so React doesn't warn.
                  key={row.member_id ?? `row-${rowIdx}`}
                  onClick={
                    interactive
                      ? () =>
                          onSelectMember?.(
                            isSelected ? null : row.member_id,
                          )
                      : undefined
                  }
                  className={cn(
                    "transition-colors duration-150",
                    interactive && "cursor-pointer hover:bg-indigo-500/[0.04]",
                    isSelected && "bg-indigo-500/[0.07]",
                  )}
                >
                  <td className="sticky left-0 bg-inherit px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6 border border-slate-200">
                        <AvatarFallback
                          className={cn(
                            "text-[10px] font-semibold",
                            avatar.bg,
                            avatar.text,
                          )}
                        >
                          {initials(row.member_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate font-medium text-slate-800">
                        {row.member_name}
                      </span>
                    </div>
                  </td>
                  {COLUMNS.map((c) => {
                    const raw = row[c.key] as number;
                    const max = maxByColumn.get(c.key) ?? 0;
                    const isLeader =
                      c.bigger_is_better && raw > 0 && raw === max;
                    let display: string;
                    if (c.format === "duration") display = formatDuration(raw);
                    else if (c.format === "percentage") {
                      display = `${(Math.round(raw * 1000) / 10).toFixed(1)}%`;
                    } else display = formatNumber(raw);
                    return (
                      <td
                        key={c.key}
                        className="px-3 py-2.5 text-right tabular-nums"
                      >
                        <span
                          className={cn(
                            isLeader
                              ? "font-semibold text-emerald-700"
                              : "text-slate-700",
                          )}
                        >
                          {display}
                        </span>
                        {c.key === "conversion_rate" && maxConv > 0 && (
                          <div className="ml-auto mt-1 h-1 w-16 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className={cn(
                                "h-full rounded-full",
                                isLeader ? "bg-emerald-500" : "bg-indigo-400",
                              )}
                              style={{
                                width: `${Math.max(2, (raw / maxConv) * 100)}%`,
                              }}
                            />
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
